import axios, { isAxiosError } from "axios";
import type { AxiosProgressEvent } from "axios";
import pRetry, { AbortError as PRetryAbortError } from "p-retry";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { getFileExtensionFromMimeType, normalizeMimeType } from "./mime-utils";
import { windowModule } from "@/modules/window";
import { ResumableUploadStore } from "./resumable-upload-store";
import type { TranscriptArrayItem } from "@/store/audio-capture/transcription/utils/types";
import parseRange from "range-parser";
import { throttle } from "lodash-es";

// ============================================================================
// Types & Constants
// ============================================================================

export interface UploadMeta {
  capturedAudioId: string;
  transcriptText: string;
  transcriptArray: TranscriptArrayItem[];
  preferredMimeType?: string;
}

export type UploadResult =
  | { success: true }
  | {
      success: false;
      error: string;
    };

interface UploadConfig {
  chunkSize?: number;
  maxAttempts?: number;
  baseBackoffMs?: number;
  progressThrottleMs?: number;
  probeOn308MissingRange?: boolean;
}

// Upload chunk configuration
const RESUMABLE_CHUNK_SIZE_BYTES = 8 * 1024 * 1024; // 8MB chunks for efficient upload
const CHUNK_ALIGNMENT_BYTES = 256 * 1024; // 256KB alignment required by GCS

// Default retry configuration
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_BACKOFF_MS = 400;
const DEFAULT_PROGRESS_THROTTLE_MS = 100;

// Retry settings for specific operations
const QUERY_OFFSET_RETRIES = 2;
const QUERY_OFFSET_MIN_TIMEOUT = 200;
const URL_GENERATION_RETRIES = 2;
const URL_GENERATION_MIN_TIMEOUT = 400;

// ----------------------------------------------------------------------------
// Pause/Resume Utilities (basic, chunk-boundary only)
// ----------------------------------------------------------------------------

/**
 * Provides pause/resume functionality for uploads at chunk boundaries.
 * Uses a promise-based waiting mechanism to pause upload processing.
 */
export class UploadPauser {
  private paused = false;
  private waiters: Array<() => void> = [];

  /**
   * Pauses the upload. Future calls to waitIfPaused() will block until resume() is called.
   */
  pause(): void {
    this.paused = true;
  }

  /**
   * Resumes the upload by resolving all waiting promises.
   */
  resume(): void {
    if (!this.paused) return;

    this.paused = false;
    const resolversToCall = this.waiters.splice(0);
    resolversToCall.forEach(resolve => resolve());
  }

  /**
   * Returns true if the upload is currently paused.
   */
  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Waits until the upload is resumed if currently paused.
   * Returns immediately if not paused.
   */
  async waitIfPaused(): Promise<void> {
    if (!this.paused) return;
    await new Promise<void>(resolve => this.waiters.push(resolve));
  }
}

// ============================================================================
// Main Upload Function
// ============================================================================

/**
 * Uploads an audio recording using resumable upload.
 *
 * @param blob - The audio file blob to upload
 * @param meta - Upload metadata including transcript and audio ID
 * @param opts - Optional configuration for progress, abort signal, and pausing
 * @returns Promise resolving to upload result with success/error status
 */
export async function uploadRecording(
  blob: Blob | null,
  meta: UploadMeta,
  opts?: {
    onProgress?: (percent: number, loaded: number, total: number) => void;
    signal?: AbortSignal;
    config?: UploadConfig;
    pauser?: UploadPauser;
  }
): Promise<UploadResult> {
  const mimeType = normalizeMimeType({ mimeTypeStr: blob?.type || meta.preferredMimeType });

  logger.info({
    message: "[upload-service] uploadRecording",
    info: {
      size: blob?.size ?? null,
      mimeType: mimeType ?? null,
      transcriptArrayLength: Array.isArray(meta.transcriptArray)
        ? meta.transcriptArray.length
        : null,
    },
  });

  if (!blob) return { success: false, error: "No audio recording available to upload" };
  if (!mimeType) return { success: false, error: "No supported MIME type found" };

  const fileExtension = getFileExtensionFromMimeType({ mimeTypeStr: mimeType });

  try {
    await uploadResumable(blob, meta, mimeType, fileExtension, opts);
    return { success: true };
  } catch (resumableError: unknown) {
    const resumableErrorMessage =
      resumableError instanceof Error ? resumableError.message : "Resumable upload failed";
    logger.error({
      message: "[upload-service] Resumable upload failed",
      info: { error: resumableErrorMessage },
    });

    await ResumableUploadStore.clear(meta.capturedAudioId);

    return { success: false, error: "File upload failed. Please try again." };
  }
}

// ============================================================================
// Upload Strategies
// ============================================================================

async function uploadResumable(
  blob: Blob,
  meta: UploadMeta,
  mimeType: string,
  fileExtension: string,
  opts?: {
    onProgress?: (percent: number, loaded: number, total: number) => void;
    signal?: AbortSignal;
    config?: UploadConfig;
    pauser?: UploadPauser;
  }
): Promise<void> {
  const origin = windowModule.getOrigin?.();
  const chunkSize = ensureAlignedChunkSize(opts?.config?.chunkSize ?? RESUMABLE_CHUNK_SIZE_BYTES);

  const tryResumableUpload = async (url: string) => {
    await ResumableUploadStore.set(meta.capturedAudioId, {
      url,
      fileSize: blob.size,
      chunkSize,
      mimeType,
      updatedAt: Date.now(),
    });
    await uploadInChunks({
      url,
      blob,
      mimeType,
      onProgress: opts?.onProgress,
      signal: opts?.signal,
      config: opts?.config,
      pauser: opts?.pauser,
      capturedAudioId: meta.capturedAudioId,
    });
    await ResumableUploadStore.clear(meta.capturedAudioId);
  };

  const stored = await ResumableUploadStore.get(meta.capturedAudioId);
  if (
    stored &&
    stored.fileSize === blob.size &&
    stored.chunkSize === chunkSize &&
    typeof stored.url === "string"
  ) {
    try {
      logger.debug({ message: "[upload-service] Trying stored resumable URL" });
      await tryResumableUpload(stored.url);
      return;
    } catch (e: unknown) {
      const status = isAxiosError(e) ? (e.response?.status as number | undefined) : undefined;
      if (status && (status === 404 || status === 410 || status === 400 || status === 403)) {
        logger.info({
          message: "[upload-service] Stored resumable URL invalid, regenerating",
          info: { status },
        });
        await ResumableUploadStore.clear(meta.capturedAudioId);
      } else {
        throw e;
      }
    }
  }

  const resumableUrl = await generateResumableUploadUrl({
    capturedAudioId: meta.capturedAudioId,
    fileExtension,
    mimeType,
    origin,
    transcriptText: meta.transcriptText,
    transcriptArray: meta.transcriptArray,
    transcriptProvider: "ASSEMBLY_AI_STREAMING",
  });

  logger.debug({
    message: "[upload-service] Get Resumable Upload URL Response",
    info: { url: resumableUrl || null, mimeType },
  });

  if (!resumableUrl) throw new Error("Failed to get resumable upload URL");

  await tryResumableUpload(resumableUrl);
}

// ============================================================================
// Chunked Upload Implementation
// ============================================================================

async function uploadInChunks(options: {
  url: string;
  blob: Blob;
  mimeType: string;
  chunkSize?: number;
  onProgress?: (percent: number, loaded: number, total: number) => void;
  signal?: AbortSignal;
  config?: UploadConfig;
  pauser?: UploadPauser;
  capturedAudioId: string;
}): Promise<void> {
  const {
    url,
    blob,
    mimeType,
    chunkSize = RESUMABLE_CHUNK_SIZE_BYTES,
    onProgress,
    signal,
    config,
    pauser,
    capturedAudioId,
  } = options;
  const totalBytes = blob.size;
  const throttleMs = config?.progressThrottleMs ?? DEFAULT_PROGRESS_THROTTLE_MS;

  const emitProgress = throttle((uploadedBytes: number) => {
    if (!onProgress || totalBytes <= 0) return;
    const capped = Math.min(uploadedBytes, totalBytes);
    const percent = Math.floor((capped / totalBytes) * 100);
    onProgress(percent, capped, totalBytes);
  }, throttleMs);

  let nextOffset = await queryResumableOffset({ url, totalBytes, mimeType }).catch(() => 0);
  nextOffset = Math.max(0, Math.min(nextOffset, totalBytes));
  emitProgress(nextOffset);

  // Throttled TTL bump to keep session fresh during long uploads
  const bumpTTL = throttle(() => {
    void ResumableUploadStore.bump(capturedAudioId);
  }, 60_000);

  while (nextOffset < totalBytes) {
    await pauser?.waitIfPaused?.();

    const lastByteIndex = Math.min(nextOffset + chunkSize, totalBytes) - 1;
    const chunk = blob.slice(nextOffset, lastByteIndex + 1);

    const resp = await putChunkWithRetry({
      url,
      data: chunk,
      headers: {
        "Content-Type": mimeType,
        "Content-Range": `bytes ${nextOffset}-${lastByteIndex}/${totalBytes}`,
      },
      signal,
      onUploadProgress: e => {
        const sentForThisRequest = e.loaded ?? 0;
        emitProgress(Math.min(totalBytes, nextOffset + sentForThisRequest));
      },
      validateStatus: status => (status >= 200 && status < 300) || status === 308,
      maxAttempts: config?.maxAttempts,
      baseDelayMs: config?.baseBackoffMs,
    });

    if (resp.status === 308) {
      let resumedOffset = extractUploadedOffset(
        resp.headers?.range as string | undefined,
        totalBytes
      );
      if (resumedOffset == null && (config?.probeOn308MissingRange ?? true)) {
        try {
          resumedOffset = await queryResumableOffset({ url, totalBytes, mimeType });
        } catch {
          // Ignore probe errors
        }
      }
      nextOffset = resumedOffset ?? lastByteIndex + 1;
      emitProgress(nextOffset);
      bumpTTL();
      continue;
    }

    if (resp.status >= 200 && resp.status < 300) {
      emitProgress(totalBytes);
      // ensure the trailing throttled call is delivered
      emitProgress.flush();
      // bump TTL once more on completion
      bumpTTL();
      bumpTTL.flush();
      return;
    }

    throw new Error(`Unexpected status during resumable upload: ${resp.status}`);
  }
}

async function queryResumableOffset(options: {
  url: string;
  totalBytes: number;
  mimeType: string;
}): Promise<number> {
  const { url, totalBytes, mimeType } = options;
  const resp = await pRetry(
    async () =>
      axios.put(url, "", {
        headers: {
          "Content-Range": `bytes */${totalBytes}`,
          "Content-Type": mimeType,
        },
        validateStatus: status => (status >= 200 && status < 300) || status === 308,
      }),
    {
      retries: QUERY_OFFSET_RETRIES,
      minTimeout: QUERY_OFFSET_MIN_TIMEOUT,
      factor: 2,
      onFailedAttempt: err => {
        const status = isAxiosError(err) ? err.response?.status : undefined;
        if (status && !shouldRetryStatus(status)) {
          throw new PRetryAbortError(err as Error);
        }
      },
    }
  );

  const nextOffset = extractUploadedOffset(resp.headers?.range as string | undefined, totalBytes);
  return Math.max(0, nextOffset ?? 0);
}

// ============================================================================
// HTTP & Retry Utilities
// ============================================================================

async function putChunkWithRetry(options: {
  url: string;
  data: Blob | ArrayBuffer | Uint8Array;
  headers: Record<string, string | number>;
  validateStatus: (status: number) => boolean;
  onUploadProgress?: (e: AxiosProgressEvent) => void;
  signal?: AbortSignal;
  maxAttempts?: number;
  baseDelayMs?: number;
}) {
  const { url, data, headers, validateStatus, onUploadProgress, signal } = options;
  const retries = Math.max(0, (options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS) - 1);
  const baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_BACKOFF_MS;
  return pRetry(
    async () =>
      axios.put(url, data, {
        headers,
        onUploadProgress,
        signal,
        validateStatus,
      }),
    {
      retries,
      minTimeout: baseDelayMs,
      factor: 2,
      randomize: true,
      onFailedAttempt: err => {
        const status = isAxiosError(err) ? err.response?.status : undefined;
        if (status && !shouldRetryStatus(status)) {
          throw new PRetryAbortError(err as Error);
        }
      },
    }
  );
}

function shouldRetryStatus(status?: number): boolean {
  if (!status) return true;
  if (status === 429) return true;
  if (status >= 500 && status < 600) return true;
  return false;
}

function extractUploadedOffset(rangeHeader: string | undefined, totalBytes: number): number | null {
  if (!rangeHeader) return null;
  const parsed = parseRange(totalBytes, rangeHeader);
  if (parsed == null || typeof parsed === "number" || parsed.length === 0) return null;
  const first = parsed[0];
  if (typeof first?.end !== "number") return null;
  const lastByte = first.end;
  if (!Number.isFinite(lastByte)) return null;
  return lastByte + 1;
}

function ensureAlignedChunkSize(size: number): number {
  if (size <= 0 || size % CHUNK_ALIGNMENT_BYTES !== 0) {
    throw new Error(
      `Invalid chunk size ${size}. Must be a positive multiple of ${CHUNK_ALIGNMENT_BYTES} bytes.`
    );
  }
  return size;
}

// ============================================================================
// URL Generation
// ============================================================================

async function generateResumableUploadUrl(params: {
  capturedAudioId: string;
  fileExtension: string;
  mimeType: string;
  origin?: string;
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
  transcriptProvider?:
    | "ASSEMBLY_AI"
    | "ASSEMBLY_AI_PRERECORDED"
    | "ASSEMBLY_AI_STREAMING"
    | "APPLE_SPEECH";
}): Promise<string | null> {
  const {
    capturedAudioId,
    fileExtension,
    mimeType,
    origin,
    transcriptText,
    transcriptArray,
    transcriptProvider,
  } = params;
  try {
    const resp = await pRetry(
      async () =>
        api.post(`/v2/voice/generate_resumable_signed_url`, {
          body: {
            captured_audio_id: capturedAudioId,
            filename: `${capturedAudioId}.${fileExtension}`,
            mime_type: mimeType,
            transcript_text: transcriptText,
            transcript_array: transcriptArray,
            transcript_provider: transcriptProvider,
            ...(origin ? { origin } : {}),
          },
        }),
      {
        retries: URL_GENERATION_RETRIES,
        minTimeout: URL_GENERATION_MIN_TIMEOUT,
        factor: 2,
        onFailedAttempt: (err: unknown) => {
          const e = err as { response?: { status?: number } } & { status?: number };
          const status = e?.response?.status ?? e?.status;
          if (status && !shouldRetryStatus(status)) {
            throw new PRetryAbortError((err as Error) ?? new Error("request failed"));
          }
        },
      }
    );
    return resp.data?.resumable_upload_url ?? null;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logger.warn({ message: "[upload-service] generateResumableUploadUrl failed", info: { msg } });
    return null;
  }
}
